
import { useState } from "react";
import { Users, Wallet, AlertCircle, TrendingUp, Search, CalendarDays } from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";
import { reportsApi } from "@/lib/api";
import { formatDate } from "@/lib/dateFormat";
import {
  Card, ExportButtons, HeaderTools, Kpi, KpiGrid, Note, RangeControls, ReportBody, ReportHeader, TableScroll,
  money, th, td, useCanSeeMoney, useRange, useReport, yearRange, type ExportColumn, type ExportSpec,
} from "../ReportKit";

interface ClientRow {
  id: number | null; name: string; phone: string | null;
  events: number; cancelled_events: number;
  received: number; received_on_cancelled: number; owed: number; credit: number;
  last_event_date: string | null; last_event_name: string | null;
  revenue?: number; gst?: number; billed?: number;
}
interface ClientReport {
  can_view_money: boolean;
  clients: ClientRow[];
  summary: {
    clients: number; events: number; cancelled_events: number;
    received: number; owed: number; clients_with_dues: number;
    revenue?: number; gst?: number; billed?: number;
  };
}

export default function ClientAnalyticsPage() {
  const canSeeMoney = useCanSeeMoney();
  const range = useRange(yearRange(0));
  const { from, to } = range.applied;
  const [search, setSearch] = useState("");

  const state = useReport<ClientReport>(
    async () => (await reportsApi.clientAnalytics({ from_date: from, to_date: to })).data,
    [from, to],
  );

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <ReportHeader
          icon={Users}
          iconClass="text-blue-600"
          title="Client Analytics"
          subtitle="Per client: events starting in the period, what they paid and still owe"
        >
          <HeaderTools>
            <RangeControls range={range} />
            {state.status !== 403 && (
              <ExportButtons
                build={() => state.data && buildExport(state.data, canSeeMoney && state.data.can_view_money, search, { from, to })}
                disabled={state.loading || !state.data}
              />
            )}
          </HeaderTools>
        </ReportHeader>

        <ReportBody state={state}>
          {(d) => <ClientsView d={d} money={canSeeMoney && d.can_view_money} search={search} setSearch={setSearch} />}
        </ReportBody>
      </div>
    </DashboardLayout>
  );
}

type SummaryItem = NonNullable<ExportSpec["summary"]>[number];

const NOTE =
  "Counts events that start in the period. Payments are matched to their event, so an advance paid before the period still counts. " +
  "Cancelled events are not billed or owed; money paid on them is shown separately.";

/** The search box matches the client's name or phone. */
const matches = (c: ClientRow, q: string) => !q || c.name.toLowerCase().includes(q) || (c.phone ?? "").includes(q);

/** Excel / Print: the headline figures and the client-wise table as shown (after any search). */
function buildExport(d: ClientReport, showMoney: boolean, search: string, period: { from: string; to: string }): ExportSpec {
  const s = d.summary;
  const q = search.trim().toLowerCase();
  const rows = d.clients.filter((c) => matches(c, q));
  const sum = (key: keyof ClientRow) => rows.reduce((t, c) => t + (Number(c[key]) || 0), 0);

  const columns: ExportColumn[] = [
    { label: "Client" },
    { label: "Phone" },
    { label: "Events", type: "number" },
    { label: "Cancelled events", type: "number" },
    ...(showMoney ? [
      { label: "Revenue (before GST)", type: "money" },
      { label: "Billed (incl. GST)", type: "money" },
    ] satisfies ExportColumn[] : []),
    { label: "Received", type: "money" },
    { label: "Received on cancelled events", type: "money" },
    { label: "Still owed", type: "money" },
    { label: "Credit", type: "money" },
    ...(showMoney ? [{ label: "Paid", type: "percent" }] satisfies ExportColumn[] : []),
    { label: "Last event", type: "date" },
    { label: "Last event name" },
  ];

  return {
    title: "Client Analytics",
    fileName: "client-analytics",
    period,
    summary: [
      { label: "Clients", value: s.clients, type: "number" },
      { label: "Events (not cancelled)", value: s.events, type: "number" },
      ...(s.cancelled_events ? [{ label: "Cancelled events", value: s.cancelled_events, type: "number" }] satisfies SummaryItem[] : []),
      ...(showMoney ? [
        { label: "Revenue (before GST)", value: s.revenue ?? 0, type: "money" },
        { label: "Billed (incl. GST)", value: s.billed ?? 0, type: "money" },
      ] satisfies SummaryItem[] : []),
      { label: "Received", value: s.received, type: "money" },
      { label: "Still owed", value: s.owed, type: "money" },
      { label: "Clients with dues", value: s.clients_with_dues, type: "number" },
    ],
    tables: [
      {
        title: "Client-wise breakdown",
        columns,
        rows: rows.map((c) => [
          c.name, c.phone ?? "", c.events, c.cancelled_events,
          ...(showMoney ? [c.revenue ?? 0, c.billed ?? 0] : []),
          c.received, c.received_on_cancelled, c.owed, c.credit,
          ...(showMoney ? [c.billed ? Math.round(Math.min(100, (c.received / c.billed) * 100) * 10) / 10 : 0] : []),
          c.last_event_date, c.last_event_name ?? "",
        ]),
        totals: rows.length ? [
          "Total", "", sum("events"), sum("cancelled_events"),
          ...(showMoney ? [sum("revenue"), sum("billed")] : []),
          sum("received"), sum("received_on_cancelled"), sum("owed"), sum("credit"),
          ...(showMoney ? [null] : []),
          null, null,
        ] : undefined,
      },
    ],
    notes: [NOTE, ...(q ? [`Only clients matching “${search.trim()}” are listed; the figures at the top cover all clients.`] : [])],
  };
}

function ClientsView({ d, money: showMoney, search, setSearch }: {
  d: ClientReport; money: boolean; search: string; setSearch: (v: string) => void;
}) {
  const s = d.summary;
  const q = search.trim().toLowerCase();
  const rows = d.clients.filter((c) => matches(c, q));
  const sum = (key: keyof ClientRow) => rows.reduce((t, c) => t + (Number(c[key]) || 0), 0);
  const cols = showMoney ? 8 : 5;

  return (
    <>
      <KpiGrid>
        <Kpi label="Clients" icon={Users} tone="blue" value={s.clients} sub={`${s.events} event${s.events === 1 ? "" : "s"}${s.cancelled_events ? ` · ${s.cancelled_events} cancelled` : ""}`} />
        {showMoney ? (
          <Kpi label="Revenue (before GST)" icon={TrendingUp} tone="emerald" value={money(s.revenue)} sub={`Billed ${money(s.billed)} incl. GST`} />
        ) : (
          <Kpi label="Events" icon={CalendarDays} tone="emerald" value={s.events} sub="not cancelled" />
        )}
        <Kpi label="Received" icon={Wallet} tone="teal" value={money(s.received)} sub="paid towards these events" />
        <Kpi label="Still owed" icon={AlertCircle} tone={s.owed > 0 ? "red" : "stone"} value={money(s.owed)} sub={`${s.clients_with_dues} client${s.clients_with_dues === 1 ? "" : "s"} with dues`} />
      </KpiGrid>

      <Card
        title="Client-wise breakdown"
        actions={
          <div className="relative w-full sm:w-56">
            <Search className="w-3.5 h-3.5 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="search"
              placeholder="Search client or phone…"
              aria-label="Search clients"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-10 pl-8 pr-3 text-sm border border-stone-200 rounded-xl outline-none focus:border-amber-400"
            />
          </div>
        }
      >
        {rows.length === 0 ? (
          <p className="text-sm text-stone-400 py-8 text-center">{d.clients.length ? "No client matches your search" : "No client events in this period"}</p>
        ) : (
          <TableScroll minWidth={showMoney ? 820 : 560}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100">
                  <th className={`${th} text-left`}>Client</th>
                  <th className={`${th} text-right`}>Events</th>
                  {showMoney && <th className={`${th} text-right`}>Revenue (before GST)</th>}
                  {showMoney && <th className={`${th} text-right`}>Billed (incl. GST)</th>}
                  <th className={`${th} text-right`}>Received</th>
                  <th className={`${th} text-right`}>Still owed</th>
                  {showMoney && <th className={`${th} text-right`}>Paid</th>}
                  <th className={`${th} text-left`}>Last event</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {rows.map((c, i) => {
                  const paidPct = c.billed ? Math.min(100, (c.received / c.billed) * 100) : 0;
                  return (
                    <tr key={c.id ?? `x${i}`} className="hover:bg-stone-50">
                      <td className={td}>
                        <div className="font-medium text-stone-800">{c.name}</div>
                        {c.phone && <div className="text-xs text-stone-400">{c.phone}</div>}
                      </td>
                      <td className={`${td} text-right text-stone-600`}>
                        {c.events}
                        {c.cancelled_events > 0 && <div className="text-[11px] text-stone-400">{c.cancelled_events} cancelled</div>}
                      </td>
                      {showMoney && <td className={`${td} text-right tabular-nums text-stone-800`}>{money(c.revenue)}</td>}
                      {showMoney && <td className={`${td} text-right tabular-nums text-stone-600`}>{money(c.billed)}</td>}
                      <td className={`${td} text-right tabular-nums text-emerald-700`}>
                        {money(c.received)}
                        {c.received_on_cancelled > 0 && <div className="text-[11px] text-stone-400">+{money(c.received_on_cancelled)} on cancelled</div>}
                      </td>
                      <td className={`${td} text-right tabular-nums font-medium ${c.owed > 0 ? "text-red-600" : "text-stone-400"}`}>
                        {c.owed > 0 ? money(c.owed) : c.credit > 0 ? <span className="text-emerald-700">Credit {money(c.credit)}</span> : "—"}
                      </td>
                      {showMoney && (
                        <td className={`${td} text-right`}>
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-14 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${paidPct >= 100 ? "bg-emerald-400" : paidPct >= 50 ? "bg-blue-400" : "bg-amber-400"}`} style={{ width: `${paidPct}%` }} />
                            </div>
                            <span className="text-xs w-9 text-right text-stone-500">{paidPct.toFixed(0)}%</span>
                          </div>
                        </td>
                      )}
                      <td className={`${td} text-xs text-stone-500`}>
                        {c.last_event_date ? formatDate(c.last_event_date) : "—"}
                        {c.last_event_name && <div className="text-stone-400 truncate max-w-[180px]">{c.last_event_name}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-stone-200 bg-stone-50 font-bold">
                  <td className={`${td} text-stone-800`}>Total{q ? " (shown)" : ""}</td>
                  <td className={`${td} text-right`}>{sum("events")}</td>
                  {showMoney && <td className={`${td} text-right tabular-nums`}>{money(sum("revenue"))}</td>}
                  {showMoney && <td className={`${td} text-right tabular-nums`}>{money(sum("billed"))}</td>}
                  <td className={`${td} text-right tabular-nums text-emerald-700`}>{money(sum("received"))}</td>
                  <td className={`${td} text-right tabular-nums text-red-600`}>{money(sum("owed"))}</td>
                  {cols === 8 && <td className={td} />}
                  <td className={td} />
                </tr>
              </tfoot>
            </table>
          </TableScroll>
        )}
      </Card>

      <Note>{NOTE}</Note>
    </>
  );
}
